import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Space } from 'common/components/antd/Space/Space';
import { EnhancedModal, Body, Footer, Header } from 'common/components/EnhancedModal/EnhancedModal';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { notifySuccess } from 'common/utils/notifier.utils';
import { EAssetColumnKeys, EAssetStatus } from 'domains/assets/enums/assets.enum';
import { createAsset } from 'domains/assets/services/assets.service';
import { TAssetEntity } from 'domains/assets/types/assets.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import {
  SFormInput,
  SFooter,
} from '../assets.page.styles';

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
};

const CreateAssetModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onCloseModalHandler = (): void => {
    onCancelModalHandler();
    navigate(ROUTES_MAPPING.PROTECTED.ASSETS);
  };

  const onFinishForm = async (values: Record<keyof TAssetEntity, string>): Promise<void> => {
    try {
      setIsLoading(true);

      const {
        alpha_code,
        asset_code,
      } = values;

      await createAsset(ENV_COUNTERPARTY_GUID, {
        alpha_code,
        asset_code,
        status: EAssetStatus.Live,
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.AssetQueryKeys].getAssetsList_MultiRole._def,
      });

      notifySuccess('assets.action.create.notification.success');

      onCancelModalHandler();

      setIsLoading(false);

      navigate(ROUTES_MAPPING.PROTECTED.ASSETS);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  return (
    <EnhancedModal open={isOpen} width={480} onCancel={onCloseModalHandler}>
      <Form onFinish={onFinishForm}>
        <Header>{t('assets.modal.title')}</Header>
        <Body>
          <Space size={16} direction="vertical">
            <SFormInput>
              <Form.Item
                label={t('assets.modal.field.alphaCode.title')}
                name={EAssetColumnKeys.AlphaCode}
              >
                <Input placeholder={t('assets.modal.field.alphaCode.placeholder') as string} />
              </Form.Item>
            </SFormInput>

            <SFormInput>
              <Form.Item
                label={t('assets.modal.field.assetCode.title')}
                name={EAssetColumnKeys.AssetCode}
              >
                <Input placeholder={t('assets.modal.field.assetCode.placeholder') as string} />
              </Form.Item>
            </SFormInput>

            <SFormInput>
              <Form.Item
                label={t('assets.modal.field.description.title')}
              >
                <Input.TextArea
                  placeholder={t('assets.modal.field.description.placeholder') as string}
                  style={{
                    height: 110,
                  }}
                />
              </Form.Item>
            </SFormInput>
          </Space>
        </Body>
        <Footer>
          <SFooter>
            <Button
              style={{
                marginRight: 12,
              }}
              onClick={onCloseModalHandler}
            >
              {t('assets.modal.button.cancel')}
            </Button>
            <Button loading={isLoading} htmlType="submit" type="primary">
              {t('assets.modal.button.create')}
            </Button>
          </SFooter>
        </Footer>
      </Form>
    </EnhancedModal>
  );
};

export default CreateAssetModal;
